import { useState } from 'react';
import { apiFetch } from '../../lib/api.js';
import { useToast } from '../Toast.js';

interface Props {
  orgId: string;
  initial: { about?: string; mission?: string; values?: Array<{ title: string; description: string }>; stats?: Array<{ label: string; value: string }> };
  onSaved: () => void;
}

export function AboutSection({ orgId, initial, onSaved }: Props) {
  const { toast } = useToast();
  const [story, setStory] = useState(initial.about ?? '');
  const [mission, setMission] = useState(initial.mission ?? '');
  const [values, setValues] = useState(initial.values ?? []);
  const [stats, setStats] = useState(initial.stats ?? []);
  const [isSaving, setIsSaving] = useState(false);

  const save = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/orgs/${orgId}/site-content/about`, {
        method: 'PUT',
        body: JSON.stringify({ story: story || undefined, mission: mission || undefined, values: values.length > 0 ? values : undefined, stats: stats.length > 0 ? stats : undefined }),
      });
      toast('success', 'About content saved — site rebuilding');
      onSaved();
    } catch { toast('error', 'Failed to save'); }
    finally { setIsSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Our Story</label>
        <textarea value={story} onChange={e => setStory(e.target.value)} rows={4}
          placeholder="Tell your story — what makes your studio special?"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-vertical" />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Mission Statement</label>
        <input type="text" value={mission} onChange={e => setMission(e.target.value)}
          placeholder="Your mission in one sentence"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
      </div>

      {/* Values */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Values</label>
        {values.map((v, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input type="text" value={v.title} placeholder="Title"
              onChange={e => { const n = [...values]; n[i] = { ...v, title: e.target.value }; setValues(n); }}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
            <input type="text" value={v.description} placeholder="Description"
              onChange={e => { const n = [...values]; n[i] = { ...v, description: e.target.value }; setValues(n); }}
              className="flex-[2] rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
            <button onClick={() => setValues(values.filter((_, j) => j !== i))} className="text-xs text-red-400 hover:text-red-600">×</button>
          </div>
        ))}
        {values.length < 10 && (
          <button onClick={() => setValues([...values, { title: '', description: '' }])}
            className="text-xs text-primary-600 hover:text-primary-800">+ Add value</button>
        )}
      </div>

      {/* Stats */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Stats</label>
        {stats.map((s, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input type="text" value={s.value} placeholder="500+"
              onChange={e => { const n = [...stats]; n[i] = { ...s, value: e.target.value }; setStats(n); }}
              className="w-20 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
            <input type="text" value={s.label} placeholder="Students trained"
              onChange={e => { const n = [...stats]; n[i] = { ...s, label: e.target.value }; setStats(n); }}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs" />
            <button onClick={() => setStats(stats.filter((_, j) => j !== i))} className="text-xs text-red-400 hover:text-red-600">×</button>
          </div>
        ))}
        {stats.length < 8 && (
          <button onClick={() => setStats([...stats, { label: '', value: '' }])}
            className="text-xs text-primary-600 hover:text-primary-800">+ Add stat</button>
        )}
      </div>

      <button onClick={save} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
        {isSaving ? 'Saving...' : 'Save About Content'}
      </button>
    </div>
  );
}
